import React from 'react';
import styled from 'styled-components';
import { FaShieldAlt, FaUserCheck, FaExclamationTriangle } from 'react-icons/fa';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  color: #ccc;
  font-size: 18px;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const SectionContent = styled.div`
  color: #ccc;
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 24px;
`;

const TermsContainer = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #333;
`;

const TermsSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TermsSubtitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const TermsText = styled.div`
  color: #ccc;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const TermsList = styled.ul`
  color: #ccc;
  font-size: 14px;
  line-height: 1.6;
  margin: 16px 0;
  padding-left: 20px;
`;

const TermsListItem = styled.li`
  margin-bottom: 8px;
`;

const LastUpdated = styled.div`
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid #333;
`;

const TermsPage = () => {
  return (
    <Container>
      <Content>
        <Header>
          <Title>Terms of Service</Title>
          <Subtitle>
            Please read these terms carefully before using Weloop. By using our service, you agree to these terms.
          </Subtitle>
        </Header>

        <TermsContainer>
          <TermsSection>
            <TermsSubtitle>1. Acceptance of Terms</TermsSubtitle>
            <TermsText>
              By accessing and using Weloop, you accept and agree to be bound by the terms and provision of this agreement. 
              If you do not agree to abide by the above, please do not use this service.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>2. Description of Service</TermsSubtitle>
            <TermsText>
              Weloop is a social media platform that allows users to share content, connect with others, and engage in 
              various social activities. Our service includes but is not limited to:
            </TermsText>
            <TermsList>
              <TermsListItem>Creating and sharing posts, images, and videos</TermsListItem>
              <TermsListItem>Following other users and building connections</TermsListItem>
              <TermsListItem>Engaging with content through likes, comments, and shares</TermsListItem>
              <TermsListItem>Private messaging with other users</TermsListItem>
              <TermsListItem>Participating in community discussions</TermsListItem>
            </TermsList>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>3. User Accounts</TermsSubtitle>
            <TermsText>
              To use certain features of Weloop, you must create an account. You are responsible for:
            </TermsText>
            <TermsList>
              <TermsListItem>Providing accurate and complete information during registration</TermsListItem>
              <TermsListItem>Maintaining the security of your account credentials</TermsListItem>
              <TermsListItem>All activities that occur under your account</TermsListItem>
              <TermsListItem>Notifying us immediately of any unauthorized use</TermsListItem>
            </TermsList>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>4. User Conduct</TermsSubtitle>
            <TermsText>
              You agree not to use Weloop to:
            </TermsText>
            <TermsList>
              <TermsListItem>Post content that is illegal, harmful, threatening, abusive, or defamatory</TermsListItem>
              <TermsListItem>Harass, bully, or intimidate other users</TermsListItem>
              <TermsListItem>Impersonate another person or entity</TermsListItem>
              <TermsListItem>Share spam, malware, or other malicious content</TermsListItem>
              <TermsListItem>Violate any applicable laws or regulations</TermsListItem>
              <TermsListItem>Attempt to gain unauthorized access to our systems</TermsListItem>
            </TermsList>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>5. Content Ownership</TermsSubtitle>
            <TermsText>
              You retain ownership of the content you post on Weloop. However, by posting content, you grant us a 
              worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and distribute your content 
              in connection with providing and improving our service.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>6. Privacy and Data Protection</TermsSubtitle>
            <TermsText>
              Your privacy is important to us. Our collection and use of personal information is governed by our 
              Privacy Policy, which is incorporated into these Terms of Service by reference.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>7. Intellectual Property</TermsSubtitle>
            <TermsText>
              Weloop and its original content, features, and functionality are owned by us and are protected by 
              international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>8. Termination</TermsSubtitle>
            <TermsText>
              We may terminate or suspend your account immediately, without prior notice, for conduct that we believe 
              violates these Terms of Service or is harmful to other users, us, or third parties.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>9. Disclaimers</TermsSubtitle>
            <TermsText>
              Weloop is provided "as is" without warranties of any kind. We do not guarantee that the service will 
              be uninterrupted, secure, or error-free.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>10. Limitation of Liability</TermsSubtitle>
            <TermsText>
              In no event shall Weloop be liable for any indirect, incidental, special, consequential, or punitive 
              damages arising out of or relating to your use of the service.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>11. Changes to Terms</TermsSubtitle>
            <TermsText>
              We reserve the right to modify these terms at any time. We will notify users of any material changes 
              by posting the new terms on this page and updating the "Last Updated" date.
            </TermsText>
          </TermsSection>

          <TermsSection>
            <TermsSubtitle>12. Contact Information</TermsSubtitle>
            <TermsText>
              If you have any questions about these Terms of Service, please contact us at legal@weloop.tech
            </TermsText>
          </TermsSection>
        </TermsContainer>

        <LastUpdated>
          Last Updated: January 15, 2024
        </LastUpdated>
      </Content>
    </Container>
  );
};

export default TermsPage; 